import React from 'react';
import { useAuth } from '../../context/AuthContext';
import { useUISettings } from '../../context/UISettingsContext';
import { Button } from '../common/Button';

export function CredentialsDisplay() {
  const { signupResult, clearSignupResult, dispatch, ACTIONS } = useAuth();
  const { showNotification } = useUISettings();

  if (!signupResult) return null;

  const handleCopyToClipboard = async (text, type) => {
    try {
      await navigator.clipboard.writeText(text);
      showNotification(`${type} copied to clipboard!`, 'success');
    } catch (err) {
      showNotification(`Failed to copy ${type}.`, 'error');
    }
  };

  const handleProceedToLogin = () => {
    // Set the token in login form
    dispatch({ type: ACTIONS.SET_TOKEN_INPUT, payload: signupResult.token });
    // Switch to login mode
    dispatch({ type: ACTIONS.SET_AUTH_MODE, payload: 'login' });
    // Clear the signup result
    clearSignupResult();
  };

  return (
    <div className="fixed inset-0 bg-gray-100 overflow-y-auto z-50 p-4">
      <div className="bg-white shadow-2xl rounded-xl p-8 w-full max-w-2xl mx-auto my-8">
        <div className="bg-yellow-100 border-l-4 border-yellow-500 p-4 mb-6">
          <p className="font-bold text-yellow-800">⚠️ PROTOTYPE NOTICE</p>
          <p className="text-yellow-700 text-sm">
            This is a proof-of-concept application. Not recommended for production use.
            Store your private key and recovery codes securely.
          </p>
        </div>
        
        <h1 className="text-3xl font-bold text-red-600 mb-4">SAVE YOUR CREDENTIALS NOW</h1>
        <p className="mb-6 text-gray-700">
          This is the <strong className="font-bold">ONLY</strong> time your private key will be displayed.
        </p>
        
        <div className="text-left mb-4 p-4 bg-gray-50 rounded-lg">
          <label className="block mb-2 font-semibold">Your UUID (Permanent)</label>
          <div className="flex">
            <input 
              type="text" 
              readOnly 
              value={signupResult.uuid} 
              className="w-full p-3 border rounded-l-md bg-gray-200 font-mono text-sm"
            />
            <button 
              onClick={() => handleCopyToClipboard(signupResult.uuid, 'UUID')} 
              className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-r-md transition"
            >
              Copy
            </button>
          </div>
        </div>

        <div className="text-left mb-4 p-4 bg-gray-50 rounded-lg">
          <label className="block mb-2 font-semibold">Your Session Token (For this login)</label>
          <div className="flex">
            <input 
              type="text" 
              readOnly 
              value={signupResult.token} 
              className="w-full p-3 border rounded-l-md bg-gray-200 font-mono text-sm"
            />
            <button 
              onClick={() => handleCopyToClipboard(signupResult.token, 'Session Token')} 
              className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-r-md transition"
            >
              Copy
            </button>
          </div>
        </div>
        
        <div className="text-left mb-6 p-4 bg-gray-50 rounded-lg">
          <label className="block mb-2 font-semibold">Your Private Key</label>
          <div className="relative">
            <textarea 
              readOnly 
              rows="8" 
              value={signupResult.privateKey} 
              className="w-full p-3 border rounded-md bg-gray-200 font-mono text-xs"
            />
            <button 
              onClick={() => handleCopyToClipboard(signupResult.privateKey, 'Private Key')} 
              className="absolute top-2 right-2 bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 text-xs rounded-md"
            >
              Copy
            </button>
          </div>
        </div>

        <div className="mb-6">
          <label className="block mb-2 font-semibold">Recovery Codes</label>
          <div className="bg-gray-100 p-4 rounded-lg font-mono text-sm space-y-2">
            {signupResult.recoveryCodes?.map((code, index) => (
              <div key={index} className="flex items-center">
                <span className="w-8 text-gray-500">{index + 1}.</span>
                <span className="flex-1">{code}</span>
                <button
                  onClick={() => handleCopyToClipboard(code, `Code ${index + 1}`)}
                  className="text-blue-500 hover:text-blue-600 text-xs px-2 py-1"
                >
                  Copy
                </button>
              </div>
            ))}
          </div>
        </div>

        <Button
          onClick={handleProceedToLogin}
          variant="success"
          fullWidth
          size="lg"
        >
          I've Saved Everything - Proceed to Login
        </Button>
      </div>
    </div>
  );
}